import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Heading,
  Loader,
  Flex,
  IconButton,
  Text,
} from '@vibe/core';
import { Add, Delete, Download, Edit } from '@vibe/icons';
import {
  getHolidayCalendars,
  deleteHolidayCalendar,
  type HolidayCalendar,
} from '../../api/holiday.api';

// ─── Props ────────────────────────────────────────────────────────────────────

interface HolidayCalendarListProps {
  onEdit: (calendar: HolidayCalendar) => void;
  onCreateNew: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Lists all holiday calendars for the current account.
 *
 * Shows calendar name, country, holiday count, default badge, and actions.
 */
const HolidayCalendarList: React.FC<HolidayCalendarListProps> = ({
  onEdit,
  onCreateNew,
}) => {
  const [calendars, setCalendars] = useState<HolidayCalendar[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCalendars = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getHolidayCalendars();
      setCalendars(data);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to load holiday calendars';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalendars();
  }, [fetchCalendars]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await deleteHolidayCalendar(id);
      setCalendars((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to delete holiday calendar';
      setError(message);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="rp-holiday-calendar-list">
        <div className="rp-holiday-calendar-list__loading">
          <Loader size={Loader.sizes.MEDIUM} />
        </div>
      </div>
    );
  }

  return (
    <div className="rp-holiday-calendar-list">
      {/* Header */}
      <Flex
        justify={Flex.justify.SPACE_BETWEEN}
        align={Flex.align.CENTER}
        className="rp-holiday-calendar-list__header"
      >
        <Heading type={Heading.types.H3}>Holiday Calendars</Heading>
        <Button
          size={Button.sizes.SMALL}
          leftIcon={Add}
          onClick={onCreateNew}
        >
          Create New
        </Button>
      </Flex>

      {error && (
        <div className="rp-holiday-calendar-list__error">
          <Text style={{ color: '#e2445c' }}>{error}</Text>
        </div>
      )}

      {/* Calendar list */}
      {calendars.length === 0 && !error && (
        <div className="rp-holiday-calendar-list__empty">
          <Text>
            No holiday calendars yet. Create one to define holidays for your
            team.
          </Text>
        </div>
      )}

      <div className="rp-holiday-calendar-list__items">
        {calendars.map((calendar) => (
          <div
            key={calendar.id}
            className="rp-holiday-calendar-list__item"
          >
            <div className="rp-holiday-calendar-list__item-info">
              <Flex align={Flex.align.CENTER} gap={Flex.gaps.XS}>
                <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
                  {calendar.name}
                </Text>
                {calendar.is_default && (
                  <span className="rp-holiday-calendar-list__badge">
                    Default
                  </span>
                )}
              </Flex>
              <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                {calendar.holidays.length} holiday(s) &middot;{' '}
                {calendar.assignedUserIds.length} user(s)
              </Text>
            </div>
            <div className="rp-holiday-calendar-list__item-actions">
              <IconButton
                icon={Download}
                size={IconButton.sizes.SMALL}
                onClick={async () => {
                  try {
                    const {
                      createWorkbook, addTitleRow, styleHeaderRow, styleDataRow,
                      setColumnWidths, downloadWorkbook, formatDateForExcel,
                    } = await import('../../utils/export-report-excel');

                    const wb = createWorkbook();
                    const ws = wb.addWorksheet(calendar.name);
                    addTitleRow(ws, `Holiday Calendar: ${calendar.name}`, '', '');

                    const headers = ['Date', 'Name'];
                    const headerRow = ws.addRow(headers);
                    styleHeaderRow(ws, headerRow);
                    setColumnWidths(ws, [14, 30]);

                    for (const h of calendar.holidays) {
                      const row = ws.addRow([
                        formatDateForExcel(h.date),
                        h.name,
                      ]);
                      styleDataRow(row);
                    }

                    await downloadWorkbook(wb, `holiday-${calendar.name}.xlsx`);
                  } catch (err) {
                    console.error('Excel export failed:', err);
                  }
                }}
                ariaLabel={`Export ${calendar.name}`}
              />
              <IconButton
                icon={Edit}
                size={IconButton.sizes.SMALL}
                onClick={() => onEdit(calendar)}
                ariaLabel={`Edit ${calendar.name}`}
              />
              <IconButton
                icon={Delete}
                size={IconButton.sizes.SMALL}
                onClick={() => handleDelete(calendar.id)}
                ariaLabel={`Delete ${calendar.name}`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HolidayCalendarList;
